import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import {
  getBackgroundCheckSubmissions,
  getBackgroundCheckStats,
  updateBackgroundCheckStatus,
  getBackgroundCheckSSN,
  getBackgroundCheckPreviousAddress,
  BackgroundCheckSubmission
} from '../api/backgroundCheckService';

const BackgroundChecks: React.FC = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedSubmission, setSelectedSubmission] = useState<BackgroundCheckSubmission | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [showSSN, setShowSSN] = useState(false);
  const [ssn, setSSN] = useState<string | null>(null);
  const [loadingSSN, setLoadingSSN] = useState(false);
  const [showPreviousAddress, setShowPreviousAddress] = useState(false);
  const [previousAddress, setPreviousAddress] = useState<{address: string; city: string; state: string; zipCode: string} | null>(null);
  const [loadingPreviousAddress, setLoadingPreviousAddress] = useState(false);

  // Get submissions
  const { data, isLoading, error } = useQuery({
    queryKey: ['backgroundCheckSubmissions', { status: statusFilter, page, limit: 20 }],
    queryFn: () => getBackgroundCheckSubmissions({ 
      status: statusFilter === 'all' ? undefined : statusFilter, 
      page, 
      limit: 20 
    }),
  });

  // Get stats
  const { data: statsData } = useQuery({
    queryKey: ['backgroundCheckStats'],
    queryFn: getBackgroundCheckStats,
  });

  const submissions = data?.data || [];
  const pagination = data?.pagination;
  const stats = statsData?.data;

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, notes }: { id: number; status: string; notes?: string }) =>
      updateBackgroundCheckStatus(id, { status, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backgroundCheckSubmissions'] });
      queryClient.invalidateQueries({ queryKey: ['backgroundCheckStats'] });
      setIsModalOpen(false);
      setSelectedSubmission(null);
      setNotes('');
      setNewStatus('');
    },
  });

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      not_started: 'bg-gray-100 text-gray-800',
      started: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-blue-100 text-blue-800',
      received: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      not_started: 'Not Started',
      started: 'Started',
      submitted: 'Submitted',
      received: 'Received',
    };
    return labels[status] || status;
  };

  const openStatusModal = (submission: BackgroundCheckSubmission) => {
    setSelectedSubmission(submission);
    setNewStatus(submission.backgroundCheckStatus);
    setNotes(submission.backgroundCheckNotes || '');
    setIsModalOpen(true);
  };

  const openDetailModal = (submission: BackgroundCheckSubmission) => {
    setSelectedSubmission(submission);
    setShowSSN(false);
    setSSN(null);
    setShowPreviousAddress(false);
    setPreviousAddress(null);
    setIsDetailModalOpen(true);
  };

  const handleRevealSSN = async () => {
    if (!selectedSubmission) return;
    
    setLoadingSSN(true);
    try {
      const response = await getBackgroundCheckSSN(selectedSubmission.id);
      setSSN(response.data.ssn);
      setShowSSN(true);
    } catch (error) {
      console.error('Error fetching SSN:', error);
      alert('Failed to retrieve SSN. Please try again.');
    } finally {
      setLoadingSSN(false);
    }
  };

  const handleRevealPreviousAddress = async () => {
    if (!selectedSubmission) return;
    
    setLoadingPreviousAddress(true);
    try {
      const response = await getBackgroundCheckPreviousAddress(selectedSubmission.id);
      setPreviousAddress({
        address: response.data.previousAddress,
        city: response.data.previousCity,
        state: response.data.previousState,
        zipCode: response.data.previousZipCode
      });
      setShowPreviousAddress(true);
    } catch (error) {
      console.error('Error fetching previous address:', error);
      alert('Failed to retrieve previous address. This candidate may not have provided a previous address.');
    } finally {
      setLoadingPreviousAddress(false);
    }
  };

  const handleStatusUpdate = () => {
    if (selectedSubmission && newStatus) {
      updateStatusMutation.mutate({
        id: selectedSubmission.id,
        status: newStatus,
        notes: notes || undefined,
      });
    }
  };

  // Helper text for status options
  const statusHelperText: Record<string, string> = {
    not_started: 'Background check request has not been acknowledged yet',
    started: 'I acknowledge that this background check request has been seen',
    submitted: 'I have submitted the candidate\'s background check form',
    received: 'I have received the candidate\'s background check, finished'
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="sm:flex sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Background Check Management</h1>
            <p className="mt-2 text-sm text-gray-600">
              Manage background check submissions and track their progress
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="rounded-md bg-gray-500 p-3">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.total}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="rounded-md bg-yellow-500 p-3">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Started</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.started}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="rounded-md bg-blue-500 p-3">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Submitted</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.submitted}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="rounded-md bg-green-500 p-3">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Received</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.received}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status Filter
                </label>
                <select
                  id="status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="all">All Submissions</option>
                  <option value="not_started">Not Started</option>
                  <option value="started">Started</option>
                  <option value="submitted">Submitted</option>
                  <option value="received">Received</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Submissions List */}
        {isLoading ? (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">Loading submissions...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="text-center text-red-600">
              <p>Error loading submissions. Please try again.</p>
            </div>
          </div>
        ) : submissions.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>No background check submissions found.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <ResponsiveTableWrapper>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                      Candidate
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                      Contact Info
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                      Submitted
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {submissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {submission.name}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                        <div className="text-sm text-gray-900">{submission.email}</div>
                        <div className="text-sm text-gray-500">{submission.phone}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                        <div className="text-sm text-gray-900">
                          {new Date(submission.backgroundCheckSubmittedAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(
                            submission.backgroundCheckStatus
                          )}`}
                        >
                          {getStatusLabel(submission.backgroundCheckStatus)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sm:px-6">
                        <div className="flex flex-col items-end gap-2 sm:flex-row sm:justify-end sm:items-center">
                          <button
                            onClick={() => openDetailModal(submission)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => openStatusModal(submission)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            Update Status
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </ResponsiveTableWrapper>
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                    disabled={page === pagination.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{((page - 1) * (pagination.limit || 20)) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(page * (pagination.limit || 20), pagination.total)}</span> of{' '}
                      <span className="font-medium">{pagination.total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        Page {page} of {pagination.pages}
                      </span>
                      <button
                        onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                        disabled={page === pagination.pages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Detail Modal */}
        {isDetailModalOpen && selectedSubmission && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsDetailModalOpen(false)}></div>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Background Check Details
                  </h3>
                  
                  <div className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Candidate Name</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedSubmission.backgroundCheckData?.fullName}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Date of Birth</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedSubmission.backgroundCheckData?.dateOfBirth}</dd>
                    </div>
                    
                    {/* SSN Section - Secure View */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <dt className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <svg className="h-4 w-4 mr-1 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Social Security Number (Sensitive)
                      </dt>
                      {!showSSN ? (
                        <button
                          onClick={handleRevealSSN}
                          disabled={loadingSSN}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                        >
                          {loadingSSN ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Loading...
                            </>
                          ) : (
                            <>
                              <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              Click to Reveal SSN
                            </>
                          )}
                        </button>
                      ) : (
                        <div>
                          <dd className="mt-1 text-lg font-mono text-gray-900 tracking-wider">
                            {ssn}
                          </dd>
                          <p className="mt-2 text-xs text-gray-600">
                            ⚠️ This access is logged for security purposes
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-gray-500">Current Address</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {selectedSubmission.backgroundCheckData?.address}<br />
                        {selectedSubmission.backgroundCheckData?.city}, {selectedSubmission.backgroundCheckData?.state} {selectedSubmission.backgroundCheckData?.zipCode}
                      </dd>
                    </div>

                    {/* Previous Address Section - Secure View */}
                    {selectedSubmission.backgroundCheckData?.hasPreviousAddress && (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <dt className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                          <svg className="h-4 w-4 mr-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          Previous Address (Last 7 Years)
                        </dt>
                        {!showPreviousAddress ? (
                          <button
                            onClick={handleRevealPreviousAddress}
                            disabled={loadingPreviousAddress}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            {loadingPreviousAddress ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Loading...
                              </>
                            ) : (
                              <>
                                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Click to Reveal Previous Address
                              </>
                            )}
                          </button>
                        ) : (
                          <div>
                            <dd className="mt-1 text-sm text-gray-900">
                              {previousAddress?.address}<br />
                              {previousAddress?.city}, {previousAddress?.state} {previousAddress?.zipCode}
                            </dd>
                            <p className="mt-2 text-xs text-blue-700">
                              ℹ️ This address is encrypted in storage - accessed for background check purposes
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="mt-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(selectedSubmission.backgroundCheckStatus)}`}>
                          {getStatusLabel(selectedSubmission.backgroundCheckStatus)}
                        </span>
                      </dd>
                    </div>
                    {selectedSubmission.backgroundCheckNotes && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Notes</dt>
                        <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedSubmission.backgroundCheckNotes}</dd>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => setIsDetailModalOpen(false)}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Update Modal */}
        {isModalOpen && selectedSubmission && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsModalOpen(false)}></div>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Update Background Check Status
                  </h3>
                  
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Candidate: <span className="font-medium">{selectedSubmission.name}</span>
                    </p>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="newStatus" className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      id="newStatus"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="not_started">Not Started</option>
                      <option value="started">Started</option>
                      <option value="submitted">Submitted</option>
                      <option value="received">Received</option>
                    </select>
                    {/* Helper text for selected status */}
                    {newStatus && statusHelperText[newStatus] && (
                      <p className="mt-2 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded p-2">
                        <svg className="inline h-4 w-4 mr-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {statusHelperText[newStatus]}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                      Notes (Optional)
                    </label>
                    <textarea
                      id="notes"
                      rows={4}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any notes about this background check..."
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleStatusUpdate}
                    disabled={updateStatusMutation.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {updateStatusMutation.isPending ? 'Updating...' : 'Update Status'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BackgroundChecks;

